<script setup>
// Teams tab for the public tournament page — mirrors the Flutter app's Teams
// tab. Lists the entrants from tournament.teams[]; each row shows the team
// (TeamCard) with a "Group X · seed N" / "Seed N" / "unseeded" subtitle and a
// "You" marker for the signed-in player's team. Group + seed come straight from
// the loaded tournament (teams[].seed and the group stages' teams[]) — no extra
// fetch, nothing fabricated.
import {computed, ref} from 'vue';
import {useAuthService} from '~/composables/useAuthService';
import {useTeamName} from '~/composables/useTeamName';
import AvatarImage from '~/components/images/AvatarImage.vue';
import {useHandicapSettingsFor} from '~/composables/useHandicapSettingsFor';
import {useHandicaps} from '~/composables/useHandicaps';

const props = defineProps({
  tournament: {type: Object, required: true},
});

// Team handicaps: labels from the tournament's rendered settings (cached).
const {settings: handicapSettings} = useHandicapSettingsFor(
    computed(() => props.tournament?.id ? {tournamentId: props.tournament.id} : null),
    computed(() => props.tournament?.handicapsEnabled === true));
const {labelFor: handicapLabelFor, chipClassFor} = useHandicaps();

const {user} = useAuthService();
const {tournamentTeamOverride} = useTeamName();
const search = ref('');

// Singles tournaments list players, not teams.
const singles = computed(() => (props.tournament?.playersPerTeam ?? 1) <= 1);
const unit = computed(() => singles.value ? 'player' : 'team');
const unitPlural = computed(() => singles.value ? 'players' : 'teams');

const handicapEntries = computed(() => (props.tournament?.teams || []).map((tt) => ({
  teamId: tt.teamId ?? tt.team?.id ?? tt.id, name: tt.team?.name ?? tt.name ?? '', value: tt.handicap ?? null,
})));
const teamsList = computed(() => {
  const teams = props.tournament?.teams || [];
  const groupStages = (props.tournament?.stages || []).filter(s => s.type === 1);
  const playerId = user.value?.playerId;

  const mapped = teams.map((tt, i) => {
    const team = tt.team || {};
    const players = team.players || [];
    const groupStage = groupStages.find(s => (s.teams || []).some(t => t.id === team.id));
    const seed = tt.seed;
    const isMe = !!playerId && players.some(p => p.id === playerId);
    const seedText = seed != null ? `seed ${seed}` : 'unseeded';
    const subtitle = groupStage
        ? `${groupStage.name} · ${seedText}`
        : (seed != null ? `Seed ${seed}` : 'Unseeded');
    // Display name from the entrant's own data (raw tournament.teams[] lack the
    // populated singlePlayer/name the stage teams carry). The admin's override
    // is on the TOURNAMENT team row (tt.name), not on the nested team — which
    // carries no actualName, so reading it there always missed a named team and
    // fell through to the player list.
    const name = tournamentTeamOverride(tt)
        || players.map(p => p.fullName).filter(Boolean).join(' & ')
        || team.name
        || 'Unknown team';
    const initials = team.initials
        || (players.length === 1 && players[0].initials)
        || name.split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
    const avatarFile = players.length === 1 ? players[0].imageFileName : (team.imageFileName || null);
    return {key: tt.id || team.id || i, team, players, name, initials, avatarFile, seed, subtitle, isMe, handicap: tt.handicap ?? null};
  });

  mapped.sort((a, b) => {
    if (a.seed != null && b.seed != null) return a.seed - b.seed;
    if (a.seed != null) return -1;
    if (b.seed != null) return 1;
    return (a.name || '').localeCompare(b.name || '');
  });
  return mapped;
});

const filteredTeams = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return teamsList.value;
  return teamsList.value.filter(e =>
      (e.name || '').toLowerCase().includes(q) ||
      (e.players || []).some(p => (p.fullName || '').toLowerCase().includes(q)));
});
</script>

<template>
  <div class="teams-tab" style="min-height: 400px;">
    <v-text-field
        v-model="search"
        :placeholder="`Search ${teamsList.length} entered ${teamsList.length === 1 ? unit : unitPlural}…`"
        prepend-inner-icon="mdi-magnify"
        density="comfortable"
        variant="outlined"
        hide-details
        class="teams-search mb-3"
    />

    <div class="tl-card teams-list">
      <div v-for="entry in filteredTeams" :key="entry.key" class="team-row">
        <div class="team-id">
          <avatar-image :size="30" :filename="entry.avatarFile" :fallback="entry.initials" class="team-avatar"/>
          <span class="team-name">{{ entry.name }}</span>
        </div>
        <div class="team-meta">
          <span v-if="handicapLabelFor(handicapSettings, entry.handicap)" class="tl-chip" :class="chipClassFor(handicapSettings, entry.handicap)">
            {{ handicapLabelFor(handicapSettings, entry.handicap) }}
          </span>
          <span v-if="entry.isMe" class="tl-chip tl-chip--brand">You</span>
          <span class="team-sub">{{ entry.subtitle }}</span>
        </div>
      </div>
      <div v-if="!filteredTeams.length" class="teams-empty">
        {{ teamsList.length ? `No ${unitPlural} match your search.` : `No ${unitPlural} have entered yet.` }}
      </div>
    </div>

    <div v-if="teamsList.length" class="teams-note">
      Showing {{ filteredTeams.length }} of {{ teamsList.length }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.teams-search {
  max-width: 420px;
}

.teams-list {
  overflow: hidden;
}

.team-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;

  & + .team-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.team-id {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.team-avatar {
  flex-shrink: 0;
}

.team-name {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.team-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.team-sub {
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
  white-space: nowrap;
}

.teams-empty {
  padding: 18px 16px;
  font-size: 13px;
  color: rgb(var(--v-theme-textSubtle));
}

.teams-note {
  margin: 10px 2px;
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}
.teams-tools { display: flex; justify-content: flex-end; margin-bottom: 8px; }
</style>
